import {
    DashboardMarkRowScope,
    DashboardMarkRule,
} from '../graphql/types/dashboard/DashboardMarkRuleTypes';
import { dashboardNowInPacific } from './dashboardPacificTime';

/**
 * 看板单元格三层标记的判定逻辑。
 *
 * - 黄色底色（含文字加粗）：命中「指标 × 行范围 × 列区间 [columnOffset, columnOffsetEnd]」
 * - 红色数字：命中「指标 × 行范围」的行内**所有日期列**，该格值越过目标值
 * - 红色边框：`columnOffset` 那一列，该格值与其「上周同日」的差值越过周环比阈值
 *
 * 红字不受列限制（同一行未达目标的格子都标红），黄底覆盖区间，红框只落在区间起始列。
 * 周环比为可选配置：规则未配 `womOperator`/`womValue` 时该指标只看目标值，不标红框。
 */

/**
 * 7 个主 HUB，对应 rowScope=NATION_AND_MAIN_HUBS。
 * 值为 formatHubDisplayLabel 之后的展示名（DB 里的 'LA' 已转为 'LAX'）。
 * 与断更看板的 HUB_DISPLAY_ORDER 不同：那个是展示排序（含 LAXS/EWRS/LAXSCBT 等子仓），
 * 这里是业务上的「7个主HUB」标记范围。
 */
export const MAIN_HUB_LABELS: ReadonlySet<string> = new Set([
    'LAX', 'EWR', 'MIA', 'ATL', 'ORD', 'DFW', 'HOU',
]);

export interface CellMark {
    yellowBg: boolean;
    redText: boolean;
    redBorder: boolean;
}

export const NO_CELL_MARK: CellMark = { yellowBg: false, redText: false, redBorder: false };

/** T-N 的美西日期（YYYY-MM-DD）。T = 今天（美西当天） */
export function pacificDateAtOffset(offsetDays: number): string {
    return dashboardNowInPacific().subtract(offsetDays, 'day').format('YYYY-MM-DD');
}

/**
 * 把单元格值解析为数字。
 *
 * 各看板的值形态不一：'12.34%' / 12345 / '1,234' / '-' / null。
 * 注意保留负号——环比类的值可能为负，去掉负号会把下降误判成上升。
 */
export function parseMarkCellValue(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '-') return null;
    const parsed = parseFloat(trimmed.replace('%', '').replace(/,/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
}

function compareByOperator(value: number, operator: '>' | '<', threshold: number): boolean {
    return operator === '>' ? value > threshold : value < threshold;
}

/** 行在标记范围内的判定所需信息，由各看板按自己的行结构适配后传入 */
export interface MarkRowInfo {
    /** 是否全美汇总行 */
    isNation: boolean;
    /** HUB 展示名（须为 formatHubDisplayLabel 之后的值）；非 HUB 行可不传 */
    hubLabel?: string;
    /** 是否 WHS 行；DSP 行自带 warehouseId，同样传 true */
    isWarehouse?: boolean;
    /** 是否展开的下钻子行 */
    isDrillDown?: boolean;
}

/**
 * 判断某行是否落在规则的行范围内。
 *
 * DSP 行（尾程看板 WHS 下的展开子行）与 WHS 行同样参与标记：DSP 行自带 warehouseId，
 * 故走 NATION_AND_ALL_HUB_WHS 分支即可命中，无需单独判定。
 */
export function isRowInMarkScope(rule: DashboardMarkRule, row: MarkRowInfo): boolean {
    if (row.isDrillDown && !rule.includeDrillDown) return false;
    if (row.isNation) return true;

    switch (rule.rowScope) {
        case DashboardMarkRowScope.NATION_AND_MAIN_HUBS:
            return row.hubLabel !== undefined && MAIN_HUB_LABELS.has(row.hubLabel);
        case DashboardMarkRowScope.NATION_AND_ALL_HUB_WHS:
            return row.hubLabel !== undefined || row.isWarehouse === true;
        default:
            return false;
    }
}

/**
 * 预计算的标记上下文。
 *
 * 大表逐格调用会造成明显卡顿（断更看板 10+ HUB × 4 层 × 15 列），
 * 故把「规则查找」与「T-N 日期换算」提前算好，render 时只做 O(1) 查表。
 */
export interface MarkRuleContext {
    ruleByField: ReadonlyMap<string, DashboardMarkRule>;
    /** fieldName → 红框判定列（区间起始列）的美西日期 */
    markDateByField: ReadonlyMap<string, string>;
    /** fieldName → 黄底区间覆盖的全部美西日期 */
    markDatesByField: ReadonlyMap<string, ReadonlySet<string>>;
    /** fieldName → 标记列的上周同日（T-N-7） */
    lastWeekDateByField: ReadonlyMap<string, string>;
    /** 去重后的所有「上周同日」，供各看板决定是否需要补充请求 */
    requiredLastWeekDates: readonly string[];
    /** 是否有任何规则生效（非 DAILY 粒度或无配置时为 false） */
    enabled: boolean;
}

export const EMPTY_MARK_RULE_CONTEXT: MarkRuleContext = {
    ruleByField: new Map(),
    markDateByField: new Map(),
    markDatesByField: new Map(),
    lastWeekDateByField: new Map(),
    requiredLastWeekDates: [],
    enabled: false,
};

/**
 * 构建标记上下文。
 *
 * @param rules 后端返回的标记规则
 * @param enabled 是否启用（非「日」粒度时传 false，规则整体不生效）
 */
export function buildMarkRuleContext(
    rules: readonly DashboardMarkRule[] | undefined,
    enabled: boolean
): MarkRuleContext {
    if (!enabled || !rules || rules.length === 0) {
        return EMPTY_MARK_RULE_CONTEXT;
    }

    const ruleByField = new Map<string, DashboardMarkRule>();
    const markDateByField = new Map<string, string>();
    const markDatesByField = new Map<string, ReadonlySet<string>>();
    const lastWeekDateByField = new Map<string, string>();
    const lastWeekDates = new Set<string>();

    rules.forEach((rule) => {
        const markDate = pacificDateAtOffset(rule.columnOffset);
        const markDates = new Set<string>();
        for (let offset = rule.columnOffset; offset <= rule.columnOffsetEnd; offset += 1) {
            markDates.add(pacificDateAtOffset(offset));
        }
        ruleByField.set(rule.fieldName, rule);
        markDateByField.set(rule.fieldName, markDate);
        markDatesByField.set(rule.fieldName, markDates);
        // 未配周环比的指标不标红框，也就不需要上周同日数据，不并入补充请求范围
        if (rule.womOperator != null && rule.womValue != null) {
            const lastWeekDate = pacificDateAtOffset(rule.columnOffset + 7);
            lastWeekDateByField.set(rule.fieldName, lastWeekDate);
            lastWeekDates.add(lastWeekDate);
        }
    });

    return {
        ruleByField,
        markDateByField,
        markDatesByField,
        lastWeekDateByField,
        requiredLastWeekDates: Array.from(lastWeekDates),
        enabled: true,
    };
}

/**
 * 给指标 tooltip 追加目标值说明，如「……的比例值。目标值为99.5%」。
 *
 * 目标值与图例、单元格标记同源于后端配置，避免改了配置后 tooltip 还写着旧阈值。
 * 无对应规则（含非「日」粒度、配置缺失）时原样返回 base，不留下多余的分隔符。
 *
 * @param separator 断更看板的 tooltip 分行展示，传 '\n'；其余看板用默认的句号
 */
export function appendTargetValueDesc(
    base: string,
    ctx: MarkRuleContext,
    fieldName: string,
    separator = '。'
): string {
    const rule = ctx.ruleByField.get(fieldName);
    return rule ? `${base}${separator}目标值为${rule.targetValue}%` : base;
}

export interface ResolveCellMarkInput {
    ctx: MarkRuleContext;
    /** 指标字段名 */
    fieldName: string;
    /** 该列的日期（美西 YYYY-MM-DD） */
    columnDate: string;
    /** 行信息 */
    row: MarkRowInfo;
    /** 当前格的值 */
    cellValue: unknown;
    /** 该格上周同日的值；取不到传 undefined，此时不标红框 */
    lastWeekValue?: unknown;
}

/**
 * 判定单元格的三层标记。
 *
 * 上周同日无数据时**不标红框**——不做 0 值兜底，否则会把「没数据」误判成「暴涨」。
 */
export function resolveCellMark(input: ResolveCellMarkInput): CellMark {
    const { ctx, fieldName, columnDate, row, cellValue, lastWeekValue } = input;
    if (!ctx.enabled) return NO_CELL_MARK;

    const rule = ctx.ruleByField.get(fieldName);
    if (!rule) return NO_CELL_MARK;
    if (!isRowInMarkScope(rule, row)) return NO_CELL_MARK;

    const yellowBg = ctx.markDatesByField.get(fieldName)?.has(columnDate) ?? false;

    const current = parseMarkCellValue(cellValue);
    if (current === null) {
        // 命中范围但该格无值：区间内仍打底色，保持「需关注」范围的视觉完整
        return { yellowBg, redText: false, redBorder: false };
    }

    const redText = compareByOperator(current, rule.targetOperator, rule.targetValue);
    if (ctx.markDateByField.get(fieldName) !== columnDate) {
        return { yellowBg, redText, redBorder: false };
    }

    // 周环比为可选配置：未配则该指标只看目标值，不标红框
    const { womOperator, womValue } = rule;
    if (womOperator == null || womValue == null) {
        return { yellowBg, redText, redBorder: false };
    }

    const previous = parseMarkCellValue(lastWeekValue);
    const redBorder =
        previous === null
            ? false
            : compareByOperator(current - previous, womOperator, womValue);

    return { yellowBg, redText, redBorder };
}

/** 单元格 td 的标记 className（黄底 / 红框作用在 td 上） */
export function markCellClassName(mark: CellMark): string {
    const classes: string[] = [];
    if (mark.yellowBg) classes.push('dashboard-mark-cell-warning');
    if (mark.redBorder) classes.push('dashboard-mark-cell-wow');
    return classes.join(' ');
}
